import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Editor from '@monaco-editor/react';
import { useQuery } from '@tanstack/react-query';
import { useState, useRef, useContext, useMemo } from 'react';
import { Button, InlineNotification, Select, Table, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { DataTransferTemplateContext } from '../../../contexts/data_transfer_template/data_transfer_template.context';
import { StudiesContext } from '../../../contexts/studies/studies.context';
export const CreateTransferTemplateBySqlEditorStep3 = () => {
    const translate = useLocalizeMessage();
    const toast = useToastNotification();
    const { entityId } = useContext(AuthContext);
    const momClient = useApiClient(ApiClientService.MOM);
    const { validateSQLQuery, createTemplateFormData, setCreateTemplateFormData, setAvailableColumns } = useContext(DataTransferTemplateContext);
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const defaultStudyOption = {
        label: translate('Select a study'),
        value: '',
    };
    const defaultEnvOption = {
        label: translate('Select an environment'),
        value: '',
    };
    const { studies } = useContext(StudiesContext);
    const [sqlQuery, setSqlQuery] = useState(createTemplateFormData.customQuery);
    const [selectedStudy, setSelectedStudy] = useState(defaultStudyOption);
    const [selectedEnv, setSelectedEnv] = useState(defaultEnvOption);
    const [isLoading, setIsLoading] = useState(false);
    const [queryResult, setQueryResult] = useState([]);
    const studyOptions = studies.map((study) => ({
        label: study.name,
        value: study.id.toString(),
    }));
    const fetchEnvs = async () => {
        try {
            setSelectedEnv(defaultEnvOption);
            const [devEnvironmentsResponse] = await Promise.all([
                momClient.get(`/v1/entities/${entityId}/studies/${selectedStudy.value}/environments`, {
                    params: {
                        clusterTypes: 'dev',
                        withCortexAccess: true,
                    },
                }),
            ]);
            return [...devEnvironmentsResponse.data.data.filter((env) => !env.deployment.isProd)];
        }
        catch (error) {
            toast.addNotification({
                type: 'error',
                title: error.message ||
                    error.error.message ||
                    translate('An error occurred while fetching environments. Please try again.'),
            });
        }
    };
    const { data: environments = [], isLoading: isFetchingEnvs } = useQuery({
        queryKey: ['entities', entityId, 'studies', selectedStudy.value, 'dev-with-cortex-access'],
        queryFn: fetchEnvs,
        enabled: !!entityId && !!selectedStudy.value,
        refetchOnWindowFocus: false,
    });
    const envOptions = environments.map((env) => ({
        label: env.name,
        value: env.cortexId,
    }));
    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;
    };
    const validateSQLSyntax = (value) => {
        if (!editorRef.current || !monacoRef.current || !value) {
            return;
        }
        const monaco = monacoRef.current;
        const editor = editorRef.current;
        const model = editor.getModel();
        if (!model) {
            return;
        }
        // Clear previous markers
        monaco.editor.setModelMarkers(model, 'sql-validation', []);
        // Simple validation checks
        const errors = [];
        // Check for basic SQL keywords
        if (value.trim() && !/(SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP)\s/i.test(value.toUpperCase())) {
            errors.push({
                severity: monaco.MarkerSeverity.Error,
                message: translate('SQL statement must start with a valid SQL keyword'),
                startLineNumber: 1,
                startColumn: 1,
                endLineNumber: 1,
                endColumn: 10,
            });
        }
        // Check for unbalanced parentheses (ignore string literals)
        const valueWithoutStrings = value.replace(/'(?:''|[^'])*'/g, '');
        const openParenCount = (valueWithoutStrings.match(/\(/g) || []).length;
        const closeParenCount = (valueWithoutStrings.match(/\)/g) || []).length;
        if (openParenCount !== closeParenCount) {
            errors.push({
                severity: monaco.MarkerSeverity.Error,
                message: translate('Unbalanced parentheses'),
                startLineNumber: 1,
                startColumn: 1,
                endLineNumber: model.getLineCount(),
                endColumn: model.getLineMaxColumn(model.getLineCount()),
            });
        }
        // Check for LIMIT exceeding 100
        const limitRegex = /LIMIT\s+(\d+)/gi;
        let match;
        while ((match = limitRegex.exec(value)) !== null) {
            const limitValue = parseInt(match[1], 10);
            if (limitValue > 100) {
                // Find the line number where the LIMIT appears
                const textUntilLimit = value.substring(0, match.index);
                const lineCount = (textUntilLimit.match(/\n/g) || []).length + 1;
                // Find the column
                const lastNewlineIndex = textUntilLimit.lastIndexOf('\n');
                const column = lastNewlineIndex === -1 ? match.index + 1 : match.index - lastNewlineIndex;
                errors.push({
                    severity: monaco.MarkerSeverity.Error,
                    message: translate('LIMIT value cannot exceed 100'),
                    startLineNumber: lineCount,
                    startColumn: column,
                    endLineNumber: lineCount,
                    endColumn: column + match[0].length,
                });
            }
        }
        // Set markers for errors
        monaco.editor.setModelMarkers(model, 'sql-validation', errors);
        return errors.length === 0;
    };
    const handleValidateSQLQuery = async (explain = false) => {
        try {
            // First perform frontend validation
            if (!validateSQLSyntax(sqlQuery)) {
                toast.addNotification({
                    type: 'error',
                    title: translate('Please fix SQL syntax errors before validating'),
                });
                return;
            }
            setIsLoading(true);
            setQueryResult([]);
            const response = await validateSQLQuery({
                sql: sqlQuery || '',
                environmentId: selectedEnv.value,
                studyId: selectedStudy.value,
                explain,
            });
            if (explain) {
                toast.addNotification({
                    type: 'success',
                    title: translate('SQL query validated successfully'),
                });
            }
            else {
                setQueryResult(response.data);
                try {
                    const cols = Array.isArray(response.data) && response.data.length > 0 ? Object.keys(response.data[0]) : [];
                    setAvailableColumns(cols);
                }
                catch {
                    setAvailableColumns([]);
                }
            }
        }
        catch (error) {
            const axiosError = error;
            const errorMessage = axiosError.response?.data?.message || translate('SQL query validation failed');
            toast.addNotification({
                type: 'error',
                title: errorMessage,
            });
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleSQLQueryChange = (value) => {
        if (value) {
            setSqlQuery(value);
            validateSQLSyntax(value);
            setCreateTemplateFormData({ ...createTemplateFormData, customQuery: value });
        }
    };
    const columns = useMemo(() => {
        return queryResult.length > 0
            ? Object.keys(queryResult[0]).map((column) => ({
                header: column,
                accessorKey: column,
            }))
            : [];
    }, [queryResult]);
    return (_jsxs("div", { className: 'grid grid-cols-2 gap-4 size-full', children: [_jsxs("div", { className: 'space-y-4 size-full flex flex-col min-w-0', children: [_jsxs("div", { className: 'space-y-4', children: [_jsx("div", { children: translate('SQL editor') }), _jsxs("div", { className: 'grid grid-cols-3 gap-4 items-end', children: [_jsx(Select, { placeholder: defaultStudyOption.label, options: studyOptions, value: selectedStudy, onChange: (option) => setSelectedStudy(option), width: 'full', searchableOptions: true }), _jsx(Select, { disabled: isFetchingEnvs || !selectedStudy.value, placeholder: defaultEnvOption.label, options: envOptions, value: selectedEnv, onChange: (option) => setSelectedEnv(option), width: 'full' }), _jsxs("div", { className: 'grid grid-cols-2 gap-4 nucleus-h-10', children: [_jsx(Button, { disabled: !selectedEnv.value || isLoading || !sqlQuery, variant: 'secondary', label: translate('Validate'), size: 'full', onClick: () => handleValidateSQLQuery(true) }), _jsx(Button, { disabled: !selectedEnv.value || isLoading || !sqlQuery, label: translate('Run'), size: 'full', onClick: () => handleValidateSQLQuery(false) })] })] })] }), _jsx("div", { children: _jsx(InlineNotification, { subtitle: translate('Max LIMIT for any query is 100'), title: '', type: 'info' }) }), _jsx("div", { className: 'border rounded-lg flex-1 py-4', children: _jsx(Editor, { height: '100%', defaultLanguage: 'sql', value: sqlQuery, onChange: handleSQLQueryChange, onMount: handleEditorDidMount, options: {
                                minimap: { enabled: false },
                                fontSize: 14,
                                formatOnType: true,
                                lineNumbers: 'on',
                                automaticLayout: true,
                            } }) })] }), _jsxs("div", { className: 'border-l px-4 space-y-4 min-w-0 min-h-0 flex flex-col', children: [_jsx("div", { children: translate('Data preview') }), queryResult.length > 0 ? (_jsx("div", { className: 'flex-1 min-h-0 overflow-y-auto', children: _jsx(Table, { columns: columns, data: queryResult, isLoading: false }) })) : (_jsx("div", { className: 'flex items-center justify-center h-full', children: _jsx("div", { className: 'text-center text-gray-500', children: translate('No data available, try running a query first.') }) }))] })] }));
};
